import React, { useState } from 'react';
import { AlertBanner } from '@ygtechnologies/rubus-ui';
import { SearchInput } from '@ygtechnologies/bygr-frontend-component-library';
import { FlatCard } from '@/components/FlatCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { ChecksState, MergedPr, OpenPr, PrState } from './activityDummyData';
import { isDone, isReadyToTest, linearColumns, useDevActivity } from './devActivity';
import { SourceBadge } from './SourceBadge';
import PrDetailsModal from './PrDetailsModal';
import ReposModal from './ReposModal';
import shared from './SystemHealthPage.module.scss';
import theme from './ActivityTab.module.scss';

type Range = 'today' | '7d';

const ranges: { id: Range; label: string; hours: number }[] = [
    { id: 'today', label: 'Last 24h', hours: 24 },
    { id: '7d', label: 'Last 7 days', hours: 24 * 7 },
];

const fmt = (n: number) => n.toLocaleString('en-GB');

const GITHUB = 'https://github.com/YGTechnologies';
const repoUrl = (repo: string) => `${GITHUB}/${repo}`;
const prUrl = (repo: string, n: number) => `${repoUrl(repo)}/pull/${n}`;
const linearUrl = (id: string) => `https://linear.app/rucoria/issue/${id}`;
const OPEN_PRS_URL = 'https://github.com/pulls?q=is%3Aopen+is%3Apr+org%3AYGTechnologies';

/** Rows shown in the open PR table and cards per Linear column; the rest is one click away. */
const PR_ROWS = 12;
const COLUMN_CARDS = 5;

/** A review request older than this is called out as stale. */
const STALE_REVIEW_HOURS = 48;

const prStateLabel: Record<PrState, string> = {
    draft: 'Draft',
    review: 'Needs review',
    changes: 'Changes requested',
    approved: 'Approved',
};
const prStateOrder: Record<PrState, number> = { approved: 0, review: 1, changes: 2, draft: 3 };
const checksLabel: Record<ChecksState, string> = {
    passing: 'Checks pass',
    failing: 'Checks fail',
    pending: 'Checks running',
};

const ageHours = (date: Date, now: number) => (now - date.getTime()) / 3_600_000;

const Ext: React.FC<React.PropsWithChildren<{ href: string; className?: string; title?: string }>> = ({
    href,
    className,
    title,
    children,
}) => (
    <a href={href} target="_blank" rel="noreferrer" className={className} title={title}>
        {children}
    </a>
);

const LinearLink: React.FC<{ id: string }> = ({ id }) => (
    <Ext href={linearUrl(id)} className={theme.linearId}>
        {id}
    </Ext>
);

const CardLink: React.FC<{ href: string; children: string }> = ({ href, children }) => (
    <Ext href={href} className={theme.cardLink}>
        {children} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
    </Ext>
);

function timeAgo(date: Date, now: number) {
    const m = Math.max(0, Math.round((now - date.getTime()) / 60_000));
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
}

const ActivityTab: React.FC<{ now: number }> = ({ now }) => {
    const { github, linear, reloadGitHub, reloadLinear } = useDevActivity();
    const { openPrs, mergedPrs } = github.data;
    const linearIssues = linear.data;
    const gh = <SourceBadge live={github.live} stale={github.stale} />;
    const [range, setRange] = useState<Range>('today');
    const [reposOpen, setReposOpen] = useState(false);
    const hours = ranges.find((r) => r.id === range)!.hours;
    const prs = mergedPrs.filter((p) => now - p.mergedAt.getTime() <= hours * 3_600_000);
    const added = prs.reduce((n, p) => n + p.additions, 0);
    const deleted = prs.reduce((n, p) => n + p.deletions, 0);
    const readyToTest = linearIssues.filter(isReadyToTest).length;
    const awaitingReview = openPrs.filter((p) => p.state === 'review');
    const oldestReview = Math.max(0, ...awaitingReview.map((p) => ageHours(p.openedAt, now)));
    const drafts = openPrs.filter((p) => p.state === 'draft').length;
    const failingChecks = openPrs.filter((p) => p.checks === 'failing').length;

    return (
        <div className={theme.tab}>
            {github.error && (
                <AlertBanner
                    type="warning"
                    title={
                        github.live
                            ? "GitHub's latest update failed; showing pull requests from an earlier one"
                            : 'Showing example pull requests until GitHub connects'
                    }
                    actions={[{ text: 'Try again', onClick: reloadGitHub }]}>
                    {github.error}
                </AlertBanner>
            )}
            {linear.error && (
                <AlertBanner
                    type="warning"
                    title={
                        linear.live
                            ? "Linear's latest update failed; showing issues from an earlier one"
                            : 'Showing example issues until Linear connects'
                    }
                    actions={[{ text: 'Try again', onClick: reloadLinear }]}>
                    {linear.error}
                </AlertBanner>
            )}

            <div className={shared.filters} role="group" aria-label="Time range">
                {ranges.map((r) => (
                    <button
                        key={r.id}
                        type="button"
                        className={shared.chip}
                        aria-pressed={range === r.id}
                        onClick={() => setRange(r.id)}>
                        {r.label}
                    </button>
                ))}
            </div>

            <div className={`${theme.row} ${theme.rowEven}`}>
                <FlatCard title="Slop meter" actions={gh} contentClassName={theme.centered}>
                    <SlopMeter added={added} deleted={deleted} />
                </FlatCard>
                <FlatCard
                    title="Code changed per repo"
                    actions={
                        <span className={theme.cardActions}>
                            {gh}
                            <button type="button" className={theme.linkButton} onClick={() => setReposOpen(true)}>
                                All repos ({github.data.repos.length})
                            </button>
                        </span>
                    }
                    contentClassName={theme.centered}>
                    <RepoBars prs={prs} />
                </FlatCard>
            </div>

            {reposOpen && <ReposModal repos={github.data.repos} now={now} onClose={() => setReposOpen(false)} />}

            <section className={shared.stats}>
                <Tile
                    label="PRs merged"
                    value={fmt(prs.length)}
                    note={`+${fmt(added)} / −${fmt(deleted)} lines`}
                    badge={gh}
                />
                <Tile
                    label="Open PRs"
                    value={openPrs.length}
                    note={`${drafts} draft${drafts === 1 ? '' : 's'}`}
                    badge={gh}
                />
                <Tile
                    label="Waiting for review"
                    value={awaitingReview.length}
                    note={
                        awaitingReview.length
                            ? `oldest ${oldestReview < 48 ? `${Math.round(oldestReview)}h` : `${Math.round(oldestReview / 24)}d`}`
                            : 'queue empty'
                    }
                    tone={oldestReview > STALE_REVIEW_HOURS ? 'bad' : undefined}
                    badge={gh}
                />
                <Tile
                    label="Failing checks"
                    value={failingChecks}
                    note="on open PRs"
                    tone={failingChecks ? 'bad' : undefined}
                    badge={gh}
                />
                <Tile
                    label="Ready to test"
                    value={readyToTest}
                    note="in Linear"
                    badge={<SourceBadge live={linear.live} stale={linear.stale} />}
                />
            </section>

            <FlatCard
                title="Open pull requests"
                actions={
                    <span className={theme.cardActions}>
                        {gh} <CardLink href={OPEN_PRS_URL}>GitHub</CardLink>
                    </span>
                }>
                <OpenPrTable prs={openPrs} now={now} />
            </FlatCard>

            <div className={theme.row}>
                <FlatCard title="Recently merged" actions={gh}>
                    {prs.length === 0 ? (
                        <p className={shared.muted}>Nothing merged in this period.</p>
                    ) : (
                        <ol className={theme.prList}>
                            {prs.map((p) => (
                                <li key={`${p.repo}#${p.number}`} className={theme.pr}>
                                    <div className={theme.prMain}>
                                        <div className={theme.prTitle}>
                                            <Ext href={repoUrl(p.repo)} className={theme.repo}>
                                                {p.repo}
                                            </Ext>
                                            <Ext href={p.url ?? prUrl(p.repo, p.number)} className={theme.prLink}>
                                                <span className={shared.muted}>#{p.number}</span> {p.title}
                                            </Ext>
                                        </div>
                                        <div className={shared.muted}>
                                            {p.author} → <span className={theme.branch}>{p.base}</span>
                                            {p.linearId && <LinearLink id={p.linearId} />}
                                        </div>
                                    </div>
                                    <div className={theme.diff}>
                                        <span className={theme.added}>+{fmt(p.additions)}</span>
                                        <span className={theme.deleted}>−{fmt(p.deletions)}</span>
                                    </div>
                                    <time className={shared.muted}>{timeAgo(p.mergedAt, now)}</time>
                                </li>
                            ))}
                        </ol>
                    )}
                </FlatCard>

                <FlatCard
                    title="Linear"
                    actions={
                        <span className={theme.cardActions}>
                            <SourceBadge live={linear.live} stale={linear.stale} />{' '}
                            <CardLink href="https://linear.app/rucoria/team/RUC/active">Linear</CardLink>
                        </span>
                    }>
                    <div className={theme.board}>
                        {linearColumns(linearIssues).map((state) => {
                            const issues = linearIssues.filter((i) =>
                                state === 'Done' ? isDone(i) : !isDone(i) && i.state === state
                            );
                            return (
                                <section key={state} className={theme.column}>
                                    <h5 className={theme.columnTitle}>
                                        {state} <span className={shared.muted}>{issues.length}</span>
                                    </h5>
                                    {issues.slice(0, COLUMN_CARDS).map((i) => (
                                        <div key={i.id} className={theme.issue}>
                                            <div className={theme.issueTitle}>{i.title}</div>
                                            <div className={shared.muted}>
                                                <LinearLink id={i.id} /> {i.assignee ?? 'Unassigned'} ·{' '}
                                                {timeAgo(i.updatedAt, now)}
                                            </div>
                                        </div>
                                    ))}
                                    {issues.length > COLUMN_CARDS && (
                                        <span className={shared.muted}>+{issues.length - COLUMN_CARDS} more</span>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                </FlatCard>
            </div>
        </div>
    );
};

const Tile: React.FC<{
    label: string;
    value: React.ReactNode;
    note?: string;
    tone?: 'added' | 'deleted' | 'bad';
    badge?: React.ReactNode;
}> = ({ label, value, note, tone, badge }) => (
    <div className={shared.stat}>
        <div className={shared.statLabel}>
            {label} {badge}
        </div>
        <div className={`${shared.statValue} ${tone ? theme[tone] : ''}`}>{value}</div>
        {note && <div className={shared.muted}>{note}</div>}
    </div>
);

// ---- open PRs ----

type PrFilter = PrState | 'all' | 'failing';

const prFilters: PrFilter[] = ['all', 'review', 'changes', 'approved', 'draft', 'failing'];
const prFilterLabel = (f: PrFilter) => (f === 'all' ? 'All' : f === 'failing' ? 'Failing checks' : prStateLabel[f]);
const matchesFilter = (p: OpenPr, f: PrFilter) => f === 'all' || (f === 'failing' ? p.checks === 'failing' : p.state === f);

const OpenPrTable: React.FC<{ prs: OpenPr[]; now: number }> = ({ prs, now }) => {
    const [filter, setFilter] = useState<PrFilter>('all');
    const [search, setSearch] = useState('');
    const [showAll, setShowAll] = useState(false);
    const [opened, setOpened] = useState<OpenPr | null>(null);
    if (prs.length === 0) return <p className={shared.muted}>No open pull requests.</p>;

    const needle = search.trim().toLowerCase();
    const matchesSearch = (p: OpenPr) =>
        !needle ||
        [p.title, p.repo, p.author, `#${p.number}`, p.linearId ?? '', ...(p.reviewers ?? [])].some((x) =>
            x.toLowerCase().includes(needle)
        );
    const searched = prs.filter(matchesSearch);
    // Closest to merging first; within a state, newest first, so long-abandoned PRs don't crowd out current work.
    // Waiting for review: oldest first, since that's the order they should be picked up in.
    const sorted = searched
        .filter((p) => matchesFilter(p, filter))
        .sort((a, b) =>
            filter === 'review'
                ? a.openedAt.getTime() - b.openedAt.getTime()
                : prStateOrder[a.state] - prStateOrder[b.state] || b.openedAt.getTime() - a.openedAt.getTime()
        );
    const rows = showAll ? sorted : sorted.slice(0, PR_ROWS);
    const hidden = sorted.length - rows.length;

    return (
        <>
            <div className={theme.prToolbar}>
                <div className={shared.filters} role="group" aria-label="Filter pull requests">
                    {prFilters.map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={shared.chip}
                            aria-pressed={filter === f}
                            onClick={() => setFilter(f)}>
                            {prFilterLabel(f)} {searched.filter((p) => matchesFilter(p, f)).length}
                        </button>
                    ))}
                </div>
                <SearchInput searchText={search} onSearchTextChange={setSearch} />
            </div>
            {sorted.length === 0 ? (
                <p className={shared.muted}>No pull requests match.</p>
            ) : (
                <table className={`${shared.table} ${theme.compactTable}`}>
                    <thead>
                        <tr>
                            <th>Pull request</th>
                            <th>Status</th>
                            <th className={shared.num}>Size</th>
                            <th className={shared.num}>Age</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p) => {
                            const stale = p.state === 'review' && ageHours(p.openedAt, now) > STALE_REVIEW_HOURS;
                            return (
                                <tr
                                    key={`${p.repo}#${p.number}`}
                                    className={p.state === 'draft' ? theme.draftRow : undefined}>
                                    <td>
                                        <button
                                            type="button"
                                            className={`${theme.linkButton} ${theme.prLink}`}
                                            onClick={() => setOpened(p)}>
                                            {p.title}
                                        </button>
                                        <Ext
                                            href={p.url ?? prUrl(p.repo, p.number)}
                                            className={theme.rowGitHub}
                                            title="Open on GitHub">
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                        </Ext>
                                        <div className={shared.muted}>
                                            <Ext href={repoUrl(p.repo)} className={theme.repo}>
                                                {p.repo}
                                            </Ext>
                                            #{p.number} · {p.author}
                                            {p.linearId && <LinearLink id={p.linearId} />}
                                            {p.reviewers?.length ? <span> · review: {p.reviewers.join(', ')}</span> : null}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={theme.statusCell}>
                                            <span className={`${theme.prState} ${theme[p.state]}`}>
                                                {prStateLabel[p.state]}
                                            </span>
                                            {p.checks && (
                                                <span
                                                    className={`${theme.checks} ${theme[p.checks]}`}
                                                    title={checksLabel[p.checks]}
                                                    aria-label={checksLabel[p.checks]}
                                                />
                                            )}
                                        </div>
                                    </td>
                                    <td className={`${shared.num} ${theme.size}`}>
                                        <span className={theme.added}>+{fmt(p.additions)}</span>{' '}
                                        <span className={theme.deleted}>−{fmt(p.deletions)}</span>
                                    </td>
                                    <td className={`${shared.num} ${stale ? theme.bad : shared.muted}`}>
                                        {timeAgo(p.openedAt, now).replace(' ago', '')}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            {hidden > 0 && (
                <p className={`${shared.muted} ${theme.footnote}`}>
                    Showing {rows.length} of {sorted.length}.{' '}
                    <button type="button" className={theme.linkButton} onClick={() => setShowAll(true)}>
                        Show all
                    </button>
                </p>
            )}
            {opened && (
                <PrDetailsModal
                    repo={opened.repo}
                    number={opened.number}
                    title={opened.title}
                    onClose={() => setOpened(null)}
                />
            )}
        </>
    );
};

// ---- slop meter ----

// Share of changed lines that are additions. All additions = 100 (pure growth), all deletions = 0.
// ponytail: additions-vs-deletions is a joke metric with a real signal; swap in churn or
// PR size if the team wants something stricter.
const slopZones = [
    { from: 0, to: 55, label: 'Tidy', className: theme.zoneTidy, hint: 'More deleted than a typical week' },
    { from: 55, to: 80, label: 'Normal', className: theme.zoneNormal, hint: 'Usual mix of new and removed code' },
    { from: 80, to: 100, label: 'Sloppy', className: theme.zoneSloppy, hint: 'Mostly new code, very little cleanup' },
];

const CX = 110;
const CY = 105;
const R = 90;

const point = (value: number, radius = R) => {
    const angle = Math.PI * (1 - value / 100);
    return [CX + radius * Math.cos(angle), CY - radius * Math.sin(angle)];
};

const arc = (from: number, to: number) => {
    const [x1, y1] = point(from);
    const [x2, y2] = point(to);
    return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
};

const SlopMeter: React.FC<{ added: number; deleted: number }> = ({ added, deleted }) => {
    const total = added + deleted;
    const score = total ? Math.round((added / total) * 100) : 0;
    const zone = slopZones.find((z) => score <= z.to) ?? slopZones[slopZones.length - 1];
    const [nx, ny] = point(score, R - 18);
    const net = added - deleted;

    return (
        <div className={theme.meter}>
            <div className={theme.gaugeWrap}>
                <svg
                    viewBox="0 0 220 120"
                    className={theme.gauge}
                    role="img"
                    aria-label={`Slop score ${score} of 100: ${zone.label}`}>
                    <path d={arc(0, 100)} className={theme.gaugeTrack} />
                    {slopZones.map((z) => (
                        // small gaps between zones keep the bands visually separate
                        <path
                            key={z.label}
                            d={arc(z.from + (z.from ? 1 : 0), z.to - (z.to < 100 ? 1 : 0))}
                            className={`${z.className} ${z === zone ? theme.zoneActive : theme.zoneIdle}`}>
                            <title>{`${z.label} (${z.from}–${z.to}): ${z.hint}`}</title>
                        </path>
                    ))}
                    <line x1={CX} y1={CY} x2={nx} y2={ny} className={theme.needle} />
                    <circle cx={CX} cy={CY} r={7} className={theme.hub} />
                </svg>
                <div className={theme.meterReadout}>
                    <span className={theme.score}>{score}</span>
                    <span className={`${theme.zoneChip} ${zone.className}`}>{zone.label}</span>
                </div>
            </div>

            <div className={theme.meterSide}>
                <dl className={theme.lineStats}>
                    <dt>Added</dt>
                    <dd className={theme.added}>+{fmt(added)}</dd>
                    <dt>Deleted</dt>
                    <dd className={theme.deleted}>−{fmt(deleted)}</dd>
                    <dt>Net</dt>
                    <dd>
                        {net >= 0 ? '+' : '−'}
                        {fmt(Math.abs(net))}
                    </dd>
                </dl>
                <ul className={theme.legend}>
                    {slopZones.map((z) => (
                        <li key={z.label} className={z === zone ? theme.legendActive : undefined}>
                            <span className={`${theme.swatch} ${z.className}`} />
                            <span className={theme.legendLabel}>{z.label}</span>
                            <span className={shared.muted}>
                                {z.from}–{z.to}
                            </span>
                        </li>
                    ))}
                </ul>
                <p className={shared.muted}>Share of changed lines that were additions. 100 means nothing was deleted.</p>
            </div>
        </div>
    );
};

// ---- code changed per repo ----

const RepoBars: React.FC<{ prs: MergedPr[] }> = ({ prs }) => {
    const byRepo = Object.values(
        prs.reduce<Record<string, { repo: string; merges: number; additions: number; deletions: number }>>((acc, p) => {
            const r = (acc[p.repo] ??= { repo: p.repo, merges: 0, additions: 0, deletions: 0 });
            r.merges++;
            r.additions += p.additions;
            r.deletions += p.deletions;
            return acc;
        }, {})
    ).sort((a, b) => b.additions + b.deletions - (a.additions + a.deletions));
    // One scale for both sides, so a 900-line deletion and a 900-line addition are the same length.
    const max = Math.max(1, ...byRepo.flatMap((r) => [r.additions, r.deletions]));

    if (byRepo.length === 0) return <p className={shared.muted}>Nothing merged in this period.</p>;

    return (
        <div className={theme.repoChart}>
            <div className={theme.repoAxis} aria-hidden>
                <span />
                <span className={theme.axisMid}>
                    <span className={theme.deleted}>← deleted</span>
                    <span className={theme.added}>added →</span>
                </span>
                <span />
                <span />
            </div>
            <ul className={theme.bars}>
                {byRepo.map((r) => (
                    <li
                        key={r.repo}
                        className={theme.barRow}
                        title={`${r.repo}: ${r.merges} merged, +${fmt(r.additions)} / −${fmt(r.deletions)}`}>
                        <Ext href={repoUrl(r.repo)} className={theme.barName}>
                            {r.repo}
                        </Ext>
                        <svg viewBox="0 0 200 10" preserveAspectRatio="none" className={theme.bar} aria-hidden>
                            <rect
                                x={100 - (r.deletions / max) * 98}
                                y={0}
                                width={(r.deletions / max) * 98}
                                height={10}
                                rx={2}
                                className={theme.barDeleted}
                            />
                            <rect
                                x={101}
                                y={0}
                                width={(r.additions / max) * 98}
                                height={10}
                                rx={2}
                                className={theme.barAdded}
                            />
                            <line x1={100.5} x2={100.5} y1={0} y2={10} className={theme.barAxis} />
                        </svg>
                        <span className={theme.barDiff}>
                            <span className={theme.added}>+{fmt(r.additions)}</span>
                            <span className={theme.deleted}>−{fmt(r.deletions)}</span>
                        </span>
                        <span className={theme.mergeCount}>
                            {r.merges} PR{r.merges > 1 ? 's' : ''}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ActivityTab;
